import React from "react";
import {GameItems} from "../State/GameData";
import {L} from "./AssemblyStyles";

type PaletteSource = { itemId: string; color: string };

type SmelterStats = {
    totalOutputPerSecond: number;
    usageCount: number;
    instanceCount: number;
};

type AssemblyPaletteProps = {
    sourcePalette: PaletteSource[];
    smelterStats: Map<string, SmelterStats>;
    onAddSetup: () => void;
    hasAssemblerBuilt: boolean;
};

export function AssemblyPalette({sourcePalette, smelterStats, onAddSetup, hasAssemblerBuilt}: AssemblyPaletteProps) {
    const itemLetter = (itemId?: string) => itemId ? (GameItems[itemId]?.name ?? "?").charAt(0).toUpperCase() : "?";

    return (
        <L.Palette>
            <L.PaletteTitle>
                <span>Palette</span>
                <L.Button onClick={onAddSetup}>New assembly setup</L.Button>
            </L.PaletteTitle>
            <L.Hint>Drag smelter outputs or assemblers onto the canvas to begin wiring a pipeline.</L.Hint>
            <L.PaletteGroup>
                <strong>Smelter Fields</strong>
                {sourcePalette.map(source => {
                    const stats = smelterStats.get(source.itemId);
                    return (
                        <L.Draggable key={source.itemId} $color={source.color} draggable
                            onDragStart={ev => {
                                ev.dataTransfer.setData("node-type", "source");
                                ev.dataTransfer.setData("item-id", source.itemId);
                            }}>
                            <L.Chip $color={source.color}>{itemLetter(source.itemId)}</L.Chip>
                            <div>
                                <div>{GameItems[source.itemId].name}</div>
                                {stats && (
                                    <L.Hint>
                                        Total output {stats.totalOutputPerSecond.toFixed(2)}/s, used in {stats.usageCount} assembly line{stats.usageCount !== 1 ? 's' : ''}
                                    </L.Hint>
                                )}
                                {!stats && <L.Hint>Smelter output node</L.Hint>}
                            </div>
                        </L.Draggable>
                    );
                })}
            </L.PaletteGroup>
            <L.PaletteGroup>
                <strong>Assemblers</strong>
                <L.Draggable $color={"#60a5fa"} draggable
                             onDragStart={ev => {
                                 ev.dataTransfer.setData("node-type", "assembler");
                             }}>
                    <L.Chip $color={"#bfdbfe"}>A</L.Chip>
                    <div>
                        <div>Circuit Assembler</div>
                        <L.Hint>Consumes 20kw/second, overclock for 4x power & 2x speed.</L.Hint>
                    </div>
                </L.Draggable>
            </L.PaletteGroup>
            <L.PaletteGroup>
                <strong>Inventory Output</strong>
                <L.Hint>Each setup includes an inventory icon on the right. Connect outputs to ship goods home.</L.Hint>
            </L.PaletteGroup>
            {!hasAssemblerBuilt && <L.Hint>Craft a Circuit Assembler in the Create Item tab to activate automation ticks.</L.Hint>}
        </L.Palette>
    );
}
